import { Cloud, CloudOff, Download, Upload, RotateCcw, Check, AlertCircle, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

/**
 * MidiSyncCard — bedieningspaneel voor server-side MIDI-config.
 * Gedeeld door de drie MIDI-pagina's (Kempenvision, Image Compo, Tournaments).
 *
 * Props:
 *   sync     : return-object van useMidiConfigSync
 *   scopeLabel : "Kempenvision" — voor de tekst
 */
export default function MidiSyncCard({ sync, scopeLabel = 'deze module' }) {
  const { status, lastSync, error, pull, push, reset } = sync
  const busy = status === 'loading'

  const meta = {
    idle:         { icon: Cloud,       text: 'Nog niet gecontroleerd',      color: 'text-muted-foreground' },
    loading:      { icon: Loader2,     text: 'Bezig…',                      color: 'text-muted-foreground' },
    synced:       { icon: Check,       text: 'Opgeslagen op de server',     color: 'text-live' },
    'local-only': { icon: CloudOff,    text: 'Alleen op dit apparaat',      color: 'text-peach' },
    error:        { icon: AlertCircle, text: error || 'Sync mislukt',       color: 'text-signal' },
  }[status] ?? { icon: Cloud, text: status, color: 'text-muted-foreground' }

  const StatusIcon = meta.icon

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
            <StatusIcon className={`h-5 w-5 ${meta.color} ${busy ? 'animate-spin' : ''}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Configuratie delen
            </div>
            <div className={`text-sm font-semibold ${meta.color}`}>{meta.text}</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Sla je mappings op de server op zodat ze op elk scherm beschikbaar zijn.
              Welke MIDI-poort je gebruikt blijft per apparaat ingesteld — die verschilt per machine.
            </p>
            {lastSync && (
              <div className="font-mono text-[10px] text-muted-foreground mt-1">
                Laatst gesynchroniseerd: {new Date(lastSync).toLocaleString('nl-BE')}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={push} disabled={busy}>
            <Upload className="h-4 w-4 mr-1.5" /> Opslaan op server
          </Button>
          <Button size="sm" variant="outline" onClick={pull} disabled={busy}>
            <Download className="h-4 w-4 mr-1.5" /> Ophalen van server
          </Button>
          <Button size="sm" variant="ghost" onClick={reset} disabled={busy} className="text-signal">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Server-config wissen
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
